import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPagination from "../../../components/eco-ui/EcoPagination";
import { downloadBlob } from "../../dp/utils/format";
import {
  APPLICATION_STATUS_LABEL,
  APPLICATION_STATUS_PILL,
  exportApplicationsApi,
  fmtDateTime,
  fmtPhone,
  getApplicationFacetsApi,
  listApplicationsApi,
  listCampaignsApi,
  RATING_LABEL,
  type ApplicantRating,
  type ApplicationFacets,
  type ApplicationListItem,
  type ApplicationSort,
  type ApplicationStatus,
  type ApplicationsQuery,
  type CampaignAd,
  type CampaignListItem,
  type Paged,
  type RatingFilter,
} from "../campanhasApi";
import ApplicationModal from "./ApplicationModal";
import RatingButtons from "./RatingButtons";

const PAGE_SIZE = 25;
const RATING_FILTERS: RatingFilter[] = ["PENDENTE", "BOM", "MEDIO", "RUIM"];
const STATUSES = Object.keys(APPLICATION_STATUS_LABEL) as ApplicationStatus[];
const SORTS: { value: ApplicationSort; label: string }[] = [
  { value: "recentes", label: "Mais recentes" },
  { value: "antigos", label: "Mais antigos" },
  { value: "avaliacao", label: "Avaliação (bom primeiro)" },
  { value: "nome", label: "Nome (A–Z)" },
];

type Filters = {
  campaignId: string;
  ratings: RatingFilter[];
  statuses: ApplicationStatus[];
  adId: string;
  city: string;
  resume: "" | "true" | "false";
  from: string;
  to: string;
  answers: Record<string, string>;
};

const EMPTY: Omit<Filters, "campaignId"> = { ratings: [], statuses: [], adId: "", city: "", resume: "", from: "", to: "", answers: {} };

type Props = {
  /** Uma campanha só (aba da campanha); ausente = todas, com o seletor de campanha. */
  campaignId?: string;
  /** Anúncios da campanha, para filtrar pela origem. */
  ads?: CampaignAd[];
};

const toggle = <T,>(list: T[], value: T) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

/**
 * Candidatos com filtros para peneirar: avaliação (com a contagem de cada uma),
 * situação, cidade, currículo, período, anúncio de origem e as respostas às
 * perguntas da campanha. A avaliação se faz na própria linha.
 */
const ApplicationsPanel: React.FC<Props> = ({ campaignId: fixedCampaign, ads }) => {
  const [filters, setFilters] = useState<Filters>({ campaignId: fixedCampaign ?? "", ...EMPTY });
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<ApplicationSort>("recentes");
  const [page, setPage] = useState(1);
  const [more, setMore] = useState(false);
  const [data, setData] = useState<Paged<ApplicationListItem> | null>(null);
  const [facets, setFacets] = useState<ApplicationFacets | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignListItem[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!fixedCampaign) listCampaignsApi().then(setCampaigns).catch(() => setCampaigns([]));
  }, [fixedCampaign]);

  // Busca só depois de uma pausa na digitação.
  useEffect(() => {
    const t = setTimeout(() => setSearch(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const query: ApplicationsQuery = useMemo(() => {
    const answers = Object.fromEntries(Object.entries(filters.answers).filter(([, v]) => v.trim()));
    return {
      ...(filters.campaignId ? { campaignId: filters.campaignId } : {}),
      ...(filters.ratings.length ? { rating: filters.ratings.join(",") } : {}),
      ...(filters.statuses.length ? { status: filters.statuses.join(",") } : {}),
      ...(filters.adId ? { adId: filters.adId } : {}),
      ...(filters.city ? { city: filters.city } : {}),
      ...(filters.resume ? { hasResume: filters.resume } : {}),
      ...(filters.from ? { from: filters.from } : {}),
      ...(filters.to ? { to: filters.to } : {}),
      ...(filters.campaignId && Object.keys(answers).length ? { answers: JSON.stringify(answers) } : {}),
      ...(search ? { q: search } : {}),
      sort,
    };
  }, [filters, search, sort]);
  const queryKey = JSON.stringify(query);

  const load = useCallback(async () => {
    const current: ApplicationsQuery = JSON.parse(queryKey);
    try {
      const [list, f] = await Promise.all([listApplicationsApi({ ...current, page, pageSize: PAGE_SIZE }), getApplicationFacetsApi(current)]);
      setData(list);
      setFacets(f);
    } catch (e: any) {
      toast.error(e?.message);
      setData({ total: 0, page: 1, pageSize: PAGE_SIZE, items: [] });
    }
  }, [queryKey, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => setPage(1), [queryKey]);

  const set = <K extends keyof Filters>(key: K, value: Filters[K]) => setFilters((prev) => ({ ...prev, [key]: value }));

  // Fora da avaliação, quantos filtros estão ligados (o "Mais filtros (n)").
  const moreCount =
    filters.statuses.length +
    (filters.adId ? 1 : 0) +
    (filters.city ? 1 : 0) +
    (filters.resume ? 1 : 0) +
    (filters.from || filters.to ? 1 : 0) +
    Object.values(filters.answers).filter((v) => v.trim()).length +
    (!fixedCampaign && filters.campaignId ? 1 : 0);
  const anyFilter = moreCount > 0 || filters.ratings.length > 0 || Boolean(search);

  const clear = () => {
    setFilters({ campaignId: fixedCampaign ?? "", ...EMPTY });
    setQ("");
  };

  const exportCsv = async () => {
    try {
      setExporting(true);
      const blob = await exportApplicationsApi(JSON.parse(queryKey));
      downloadBlob(blob, `candidatos-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (e: any) {
      toast.error(e?.message);
    } finally {
      setExporting(false);
    }
  };

  // Avaliou na linha: atualiza a linha na hora e as contagens em seguida.
  const rated = (id: string, rating: ApplicantRating | null) => {
    setData((prev) => (prev ? { ...prev, items: prev.items.map((a) => (a.id === id ? { ...a, rating } : a)) } : prev));
    getApplicationFacetsApi(JSON.parse(queryKey)).then(setFacets).catch(() => undefined);
  };

  const origin = (a: ApplicationListItem) => (a.adPosition ? `Anúncio ${a.adPosition}` : "Link direto");
  const ratingPill = (r: ApplicantRating | null) => (
    <span className={`cmpRatingPill cmpRatingPill--${(r ?? "PENDENTE").toLowerCase()}`}>{RATING_LABEL[r ?? "PENDENTE"]}</span>
  );

  return (
    <>
      <div className="cmpFilters">
        <div className="cmpFilters__top">
          <label className="ecoField cmpFilters__search">
            <span className="ecoLabel">Buscar</span>
            <input className="ecoInput" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, CPF, celular, cidade, bairro ou observação" />
          </label>
          <label className="ecoField">
            <span className="ecoLabel">Ordenar por</span>
            <select className="ecoSelect" value={sort} onChange={(e) => setSort(e.target.value as ApplicationSort)}>
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="ecoBtn" onClick={exportCsv} disabled={exporting || !data?.total}>
            <EcoIcon name="download" size={18} />
            {exporting ? "Exportando..." : "Exportar CSV"}
          </button>
        </div>

        <div className="cmpChips" role="group" aria-label="Filtrar por avaliação">
          <span className="ecoLabel">Avaliação</span>
          {RATING_FILTERS.map((r) => (
            <button
              key={r}
              type="button"
              className={`cmpChip cmpChip--${r.toLowerCase()}${filters.ratings.includes(r) ? " is-active" : ""}`}
              aria-pressed={filters.ratings.includes(r)}
              onClick={() => set("ratings", toggle(filters.ratings, r))}
            >
              {RATING_LABEL[r]}
              <span className="cmpChip__count">{facets ? facets.ratings[r] : "…"}</span>
            </button>
          ))}
          <button type="button" className="ecoBtn ecoBtn--sm cmpFilters__more" onClick={() => setMore((v) => !v)} aria-expanded={more}>
            <EcoIcon name={more ? "arrowUp" : "arrowDown"} size={16} />
            Mais filtros{moreCount ? ` (${moreCount})` : ""}
          </button>
          {anyFilter ? (
            <button type="button" className="ecoLinkBtn" onClick={clear}>
              Limpar filtros
            </button>
          ) : null}
        </div>

        {more ? (
          <div className="ecoFilters__grid">
            {!fixedCampaign ? (
              <label className="ecoField">
                <span className="ecoLabel">Campanha</span>
                <select className="ecoSelect" value={filters.campaignId} onChange={(e) => setFilters((prev) => ({ ...prev, campaignId: e.target.value, answers: {}, city: "" }))}>
                  <option value="">Todas</option>
                  {campaigns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <div className="ecoField">
              <span className="ecoLabel">Situação</span>
              <div className="cmpChecks">
                {STATUSES.map((s) => (
                  <label key={s} className="cmpCheck">
                    <input type="checkbox" checked={filters.statuses.includes(s)} onChange={() => set("statuses", toggle(filters.statuses, s))} />
                    {APPLICATION_STATUS_LABEL[s]}
                  </label>
                ))}
              </div>
            </div>
            <label className="ecoField">
              <span className="ecoLabel">Cidade</span>
              <select className="ecoSelect" value={filters.city} onChange={(e) => set("city", e.target.value)}>
                <option value="">Todas</option>
                {(facets?.cities ?? []).map((c) => (
                  <option key={c.city} value={c.city}>
                    {c.city} ({c.count})
                  </option>
                ))}
              </select>
            </label>
            <label className="ecoField">
              <span className="ecoLabel">Currículo</span>
              <select className="ecoSelect" value={filters.resume} onChange={(e) => set("resume", e.target.value as Filters["resume"])}>
                <option value="">Tanto faz</option>
                <option value="true">Com currículo</option>
                <option value="false">Sem currículo</option>
              </select>
            </label>
            <label className="ecoField">
              <span className="ecoLabel">Recebida de</span>
              <input className="ecoInput" type="date" value={filters.from} max={filters.to || undefined} onChange={(e) => set("from", e.target.value)} />
            </label>
            <label className="ecoField">
              <span className="ecoLabel">Até</span>
              <input className="ecoInput" type="date" value={filters.to} min={filters.from || undefined} onChange={(e) => set("to", e.target.value)} />
            </label>
            {ads?.length ? (
              <label className="ecoField">
                <span className="ecoLabel">Anúncio de origem</span>
                <select className="ecoSelect" value={filters.adId} onChange={(e) => set("adId", e.target.value)}>
                  <option value="">Todos</option>
                  {ads.map((ad) => (
                    <option key={ad.id} value={ad.id}>
                      Anúncio {ad.position} · {ad.headline}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {filters.campaignId
              ? (facets?.questions ?? []).map((question) =>
                  question.type === "SIM_NAO" ? (
                    <label key={question.id} className="ecoField">
                      <span className="ecoLabel">{question.label}</span>
                      <select
                        className="ecoSelect"
                        value={filters.answers[question.id] ?? ""}
                        onChange={(e) => set("answers", { ...filters.answers, [question.id]: e.target.value })}
                      >
                        <option value="">Tanto faz</option>
                        <option value="SIM">Sim</option>
                        <option value="NAO">Não</option>
                      </select>
                    </label>
                  ) : (
                    <label key={question.id} className="ecoField">
                      <span className="ecoLabel">{question.label}</span>
                      <input
                        className="ecoInput"
                        value={filters.answers[question.id] ?? ""}
                        onChange={(e) => set("answers", { ...filters.answers, [question.id]: e.target.value })}
                        placeholder="Resposta contém..."
                      />
                    </label>
                  ),
                )
              : null}
            {!filters.campaignId && !fixedCampaign ? (
              <p className="cmpHint" style={{ alignSelf: "end" }}>
                Escolha a campanha para filtrar também pelas respostas das perguntas dela.
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {!data ? (
        <p className="cmpLoading">Carregando...</p>
      ) : !data.items.length ? (
        <div className="cmpPanel__pad">
          <p className="ecoEmpty">{anyFilter ? "Nenhum candidato com esses filtros." : "Nenhum candidato ainda."}</p>
        </div>
      ) : (
        <>
          <p className="cmpHint cmpFilters__total">
            {data.total} candidato(s){anyFilter ? " com esses filtros" : ""}
          </p>
          <div className="ecoTableWrap cmpDesktop">
            <table className="ecoTable">
              <thead>
                <tr>
                  <th scope="col">Candidato</th>
                  {fixedCampaign ? null : <th scope="col">Campanha</th>}
                  <th scope="col">Celular</th>
                  <th scope="col">Cidade e bairro</th>
                  <th scope="col">Origem</th>
                  <th scope="col">Currículo</th>
                  <th scope="col">Situação</th>
                  <th scope="col">Avaliação</th>
                  <th scope="col">Recebida em</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((a) => (
                  <tr key={a.id} className="cmpRow" onClick={() => setOpenId(a.id)}>
                    <td>
                      <button type="button" className="ecoLinkBtn cmpStrong" onClick={() => setOpenId(a.id)}>
                        {a.fullName}
                      </button>
                      <span className="cmpSub">{a.cpfMasked}</span>
                    </td>
                    {fixedCampaign ? null : <td className="ecoTable__muted">{a.campaign.name}</td>}
                    <td>{fmtPhone(a.phone)}</td>
                    <td className="ecoTable__muted">
                      {a.city} · {a.neighborhood}
                    </td>
                    <td className="ecoTable__muted">{origin(a)}</td>
                    <td>{a.hasResume ? "Sim" : "Não"}</td>
                    <td>
                      <span className={APPLICATION_STATUS_PILL[a.status]}>{APPLICATION_STATUS_LABEL[a.status]}</span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <RatingButtons applicationId={a.id} value={a.rating} onChange={(r) => rated(a.id, r)} />
                    </td>
                    <td className="ecoTable__muted">{fmtDateTime(a.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="cmpCards cmpMobile">
            {data.items.map((a) => (
              <li key={a.id} className="cmpCard" onClick={() => setOpenId(a.id)}>
                <span className="cmpCard__top">
                  <span>
                    <span className="cmpCard__title">{a.fullName}</span>
                    <span className="cmpSub">
                      {fmtPhone(a.phone)} · {a.city}
                    </span>
                  </span>
                  {ratingPill(a.rating)}
                </span>
                <span className="cmpCard__meta">
                  {fixedCampaign ? "" : `${a.campaign.name} · `}
                  {APPLICATION_STATUS_LABEL[a.status]} · {origin(a)} · {a.hasResume ? "com currículo" : "sem currículo"} · {fmtDateTime(a.createdAt)}
                </span>
                <RatingButtons applicationId={a.id} value={a.rating} onChange={(r) => rated(a.id, r)} size="md" />
              </li>
            ))}
          </ul>
          <div className="cmpPanel__pad">
            <EcoPagination page={data.page} pageSize={data.pageSize} total={data.total} onChange={setPage} />
          </div>
        </>
      )}

      {openId ? (
        <ApplicationModal
          id={openId}
          onClose={() => setOpenId(null)}
          onSaved={() => {
            setOpenId(null);
            load();
          }}
        />
      ) : null}
    </>
  );
};

export default ApplicationsPanel;
